import { useState, useCallback } from 'react';
import { message } from 'antd';
import { p930hApi, F90H } from '../../../services/p930hApi';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useP930HMode';
import { dateToInput } from '../helpers';

export function useP930HRecord() {
  const { user } = useAuth();
  const [record, setRecord] = useState<F90H | null>(null);
  const [formData, setFormData] = useState<Partial<F90H>>({});
  const [loading, setLoading] = useState(false);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by date string */
  const loadRecord = useCallback(async (dateStr: string): Promise<F90H | null> => {
    setLoading(true);
    try {
      const data = await p930hApi.get(dateStr);
      setRecord(data);
      setFormData({ ...data });
      return data;
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single field */
  const setField = useCallback((key: keyof F90H, value: F90H[keyof F90H]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single field value */
  const getField = useCallback((key: keyof F90H): F90H[keyof F90H] => {
    return (formData as Record<string, F90H[keyof F90H]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: F90H) => void,
  ) => {
    setLoading(true);
    clearFieldErrors();
    try {
      if (mode === 'add') {
        const dateVal = dateToInput(formData.f90h_01 as string);
        const result = await p930hApi.create({
          f90h_01: dateVal,
          f90h_11: (formData.f90h_11 as string) || '',
          f90h_12: formData.f90h_12 != null ? formData.f90h_12 as number : undefined,
          f90h_13: (formData.f90h_13 as string) || '',
          f90h_14: (formData.f90h_14 as string) || '',
          f90h_15: (formData.f90h_15 as string) || '',
          f90h_21: (formData.f90h_21 as string) || '',
          f90h_22: (formData.f90h_22 as string) || '',
          f90h_23: (formData.f90h_23 as string) || '',
          f90h_24: (formData.f90h_24 as string) || '',
        });
        message.success('新增成功');
        onSuccess(result);
      } else {
        const dateKey = dateToInput(record?.f90h_01 ?? '');
        const result = await p930hApi.update(dateKey, {
          f90h_11: (formData.f90h_11 as string) || '',
          f90h_12: formData.f90h_12 != null ? formData.f90h_12 as number : undefined,
          f90h_13: (formData.f90h_13 as string) || '',
          f90h_14: (formData.f90h_14 as string) || '',
          f90h_15: (formData.f90h_15 as string) || '',
          f90h_21: (formData.f90h_21 as string) || '',
          f90h_22: (formData.f90h_22 as string) || '',
          f90h_23: (formData.f90h_23 as string) || '',
          f90h_24: (formData.f90h_24 as string) || '',
        });
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, record, clearFieldErrors, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      const dateKey = dateToInput(record.f90h_01 ?? '');
      await p930hApi.delete(dateKey);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Revert to saved record */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
    }
  }, [record, clearFieldErrors]);

  /** Reset form for add mode */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      f90h_91: user?.userId || '',
    });
  }, [user, clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: F90H) => {
    setRecord(result);
    setFormData({ ...result });
  }, []);

  return {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
  };
}
